import React, {Component} from 'react';
import './TaskCard.css';
import {App} from '../../app/AppSingleton';
import {AppSignals} from '../../app/AppSignals';
import type {Board, IsEditing, Task} from '../../models/types';
import {ViewMode, ViewModeEnum} from '../../models/view-types';
import type {TaskEditController} from '../../app/TaskEditController';
import InlineTaskEditor from './InlineTaskEditor';
import {DndSystem} from '../../dnd-system/core/DndSystem';
import type {DragData, DraggableConfig, DraggableContext, DraggableStrategy, DropResult} from '../../dnd-system/types';
import {Icons} from "../../constants/icons";
import {Backdrop} from "./Backdrop";
import {Disposer, Signals} from "../../app/Signals";
import {IsDeviceMobile} from "../../utils/DeviceDetector";
import {DRAG_DEFAULT_CONFIG} from "../../dnd-system";

interface TaskCardProps {
	task: Task;
	index: number;
}

interface TaskCardState {
	viewMode: ViewMode;

	isExpanded: boolean;
	isEditing: boolean;
	isDragging: boolean;
	isActivating: boolean;
	isHighlighted: boolean; // Состояние для подсветки задачи
	isNewTask: boolean; // Признак новой задачи

	board: Board; // Добавляем состояние доски для проверки режима редактирования

}

type SyncStatus = { text: string, color: string, bgColor: string, icon: string, className: string };

interface CardActionSpec {
	id: string | number;
	icon: any;
	title?:string;
	onClick?: (e:React.MouseEvent) => void;
	disabled?: boolean;
	ariaLabel?:string;
	className?:string;
}
class TaskCard extends Component<TaskCardProps, TaskCardState> {
	private app: App;
	private taskEditController: TaskEditController;
	private dndSystem = DndSystem.getInstance();
	private cardRef: React.RefObject<HTMLDivElement>;
	private disposer = new Disposer();
	private dndUnregister?: () => void;

	constructor(props: TaskCardProps) {
		super(props);

		this.app = App.getInstance();
		this.taskEditController = this.app.getTaskEditController();
		this.cardRef = React.createRef();

		this.state = {
			viewMode: this.app.getViewMode(),

			isExpanded: false,
			isEditing: false,
			isDragging: false,
			isActivating: false,
			isHighlighted: false, // Инициализируем состояние подсветки
			isNewTask: false, // Инициализируем состояние новой задачи

			board: this.getBoard(), // Инициализируем состояние доски
		};
	}

	private getBoard() {
		let board = this.app.getBoardById(this.props.task.boardId);
		if (!board){
			console.error(`"Board '${this.props.task.boardId}' not found`, this.props);
			throw new Error(`"Board '${this.props.task.boardId}' not found`);
		}
		return board;
	}

	componentDidMount() {
		// Подписываемся на изменение режима просмотра
		this.disposer.register(AppSignals.viewModeChanged, (mode: ViewMode) => {
			this.setState({viewMode: mode});
		});

		// Подписываемся на завершение редактирования
		this.disposer.register(this.taskEditController.onEditEnd, () => {
			// Сохраняем состояние подсветки при завершении редактирования
			this.setState(prevState => ({isEditing: false}));
		});

		// Подписываемся на обновление доски для отслеживания режима редактирования
		this.disposer.register(AppSignals.boardUpdated, () => {
			this.forceUpdate();

			// Если доска перешла в режим редактирования, а мы редактируем задачу - отменяем
			if (this.getBoard().isEditing && this.state.isEditing) {
				this.taskEditController.cancelEdit();
				this.setState(prevState => ({isEditing: false}));
			}

			// Перерегистрируем DnD с новым состоянием
			this.unregisterDnd();
			this.registerDnd();
		});

		// Подписываемся на сигнал подсветки и скролла к задаче
		this.disposer.register(AppSignals.highlightAndScrollToTask, (data) => {
			// Проверяем совпадение по актуальному ID или по временному ID
			if (data.taskId === this.props.task.id
				|| data.tempId === this.props.task.id
				|| data.tempId === this.props.task.localId
				// || data.columnId === this.props.task.columnId
			) {
				this.highlightAndScroll();
			}
		});
		if ( this.app.getTaskHighlight(this.props.task)){
			//global state changed, new task created.
			this.highlightAndScroll();
		}

		this.disposer.register(AppSignals.taskUpdated, (task)=>{
			if (task.id !== this.props.task.id) return;
			this.forceUpdate();
		});

		// Регистрируем DnD когда элемент доступен
		this.registerDnd();
	}

	componentDidUpdate(prevProps: TaskCardProps, prevState:TaskCardState) {
		// Если изменился индекс, перерегистрируем DnD
		if (prevProps.index !== this.props.index && this.cardRef.current
			||prevState.isEditing !== this.state.isEditing
		) {
			this.unregisterDnd();
			this.registerDnd();
		}
	}

	componentWillUnmount() {
		// Отписываемся от всех подписок через Disposer
		this.disposer.dispose();

		// Отменяем регистрацию DnD
		this.unregisterDnd();
	}

	private highlightAndScroll(): void {
		// Устанавливаем состояние подсветки
		//this.setState({ isHighlighted: true });

		// Прокручиваем к элементу с небольшой задержкой, чтобы убедиться, что DOM обновлен
		setTimeout(() => {
			if (this.cardRef.current) {
				// Прокручиваем к элементу
				this.cardRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest'
				});
			}
		}, 100); // Небольшая задержка для гарантии отрисовки

		// Убираем состояние подсветки через 3 секунды
		setTimeout(() => {
			this.app.setTaskHighlight(this.props.task, false);
			AppSignals.taskUpdated.emit(this.props.task);
		}, 1000);
	}

	private registerDnd() {
		if (!this.cardRef.current) return;

		const {task} = this.props;
		const editState = this.taskEditController.state;

		// Не регистрируем DnD если редактируем задачу или доску находится в режиме редактирования
		const board = this.getBoard();
		if (editState.isEditing || board.isEditing) {
			//console.log("dnd ignore - task editing or board editing");
			return;
		}

		const element = this.cardRef.current;

		const context: DraggableContext = {
			element,
			id: task.id,
			type: 'task',
			data: {
				taskId: task.id,
				columnId: task.columnId,
				index: this.props.index
			}
		};

		const strategy: DraggableStrategy = {
			cssClasses: {
				activating: 'task-card--touch-active',
				dragging: 'task-card--dragging'
			},

			onActivate: (context: DraggableContext) => {
				this.setState({isActivating: true});
				context.element.classList.add('task-card--touch-active');
			},

			onDragStart: (context: DraggableContext, dragData: DragData) => {
				this.setState({
					isActivating: false,
					isDragging: true
				});
				AppSignals.taskDragStarted.emit(dragData);
				context.element.classList.remove('task-card--touch-active');
				context.element.classList.add('task-card--dragging');
			},

			onDragEnd: (context: DraggableContext, result: DropResult) => {
				this.setState({
					isDragging: false,
					isActivating: false
				});
				AppSignals.taskDragEnded.emit(result);
				context.element.classList.remove('task-card--touch-active', 'task-card--dragging');
			},

			onDragCancel: (context: DraggableContext) => {
				this.setState({
					isActivating: false,
					isDragging: false
				});
				context.element.classList.remove('task-card--touch-active', 'task-card--dragging');
			},

			onScrollActivation: (context: DraggableContext) => {
				this.setState({isActivating: false});
				context.element.classList.remove('task-card--touch-active');
				console.log('Scroll activated instead of DnD');
			},
			onShortClick:(context : DraggableContext, event)=>{
				event.stopPropagation();
				this.onEditInline();
			}

		};

		const config: DraggableConfig = {
			...DRAG_DEFAULT_CONFIG,
			preventScroll: true,
			disabled: editState.isEditing
		};

		this.dndUnregister = this.dndSystem.registerDraggable({
			context,
			strategy,
			config
		});
	}

	private unregisterDnd() {
		if (this.dndUnregister) {
			this.dndUnregister();
			this.dndUnregister = undefined;
		}
	}

	// Статус синхронизации
	getSyncStatus = (task: Task): SyncStatus => {
		if (task.isSynced && task.serverId) {
			return {
				text: 'синхронизировано',
				color: 'text-green-600',
				bgColor: 'bg-green-100',
				icon: '✓',
				className: 'task-card__sync-status--synced',
			};
		} else if (!task.isSynced && task.serverId) {
			return {
				text: 'изменения не синхронизированы',
				color: 'text-yellow-600',
				bgColor: 'bg-yellow-100',
				icon: '↻',
				className: 'task-card__sync-status--pending',
			};
		} else {
			return {
				text: 'только локально',
				color: 'text-gray-600',
				bgColor: 'bg-gray-100',
				icon: '💾',
				className: 'task-card__sync-status--local',
			};
		}
	};

	// Методы для редактирования
	onEditInline = () => {
		// Блокируем редактирование если доска в режиме редактирования
		if ((this.state.board  as IsEditing).isEditing) {
			console.log("Редактирование карточки отключено в режиме правки доски");
			return;
		}
		
		this.setState({isEditing: true});
		this.taskEditController.startInlineEdit(this.props.task.id, 'title').catch(console.error);
	};

	onEditFull = () => {
		// Блокируем редактирование если доска в режиме редактирования
		if ((this.state.board  as IsEditing).isEditing) {
			console.log("Редактирование карточки отключено в режиме правки доски");
			return;
		}
		
		console.log('Открыть полный редактор для задачи:', this.props.task.id);
		this.taskEditController.startEdit(this.props.task.id).catch(console.error);
	};

	handleDoubleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		this.onEditInline();
	}

	handleTitleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		this.onEditInline();
	};

	handleFullEditClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		this.onEditFull();
	};

	handleAttachmentClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		console.log('Открыть вложения для задачи:', this.props.task.id);
	};

	handleSaveTask = async (newTitle: string): Promise<void> => {
		try {
			await this.taskEditController.updateTask({
				title: newTitle.trim(),
			});
		} catch (error) {
			console.error('Error updating task:', error);
			throw error;
		}
	}

	handleDeleteTask = async (): Promise<void> => {
		try {
			await this.taskEditController.deleteTask();
		} catch (error) {
			console.error('Error deleting task:', error);
			throw error;
		}
	}

	handleCloseEditor = () => {
		this.taskEditController.cancelEdit();
		this.setState({isEditing: false});
	};

	toggleDescription = () => {
		this.setState(prevState => ({
			isExpanded: !prevState.isExpanded
		}));
	};

	render() {
		// Собираем модификаторы для корневого элемента по БЭМ
		const classBlock = `task-card`;
		const rootModifiers = [
			`${classBlock}--${this.state.viewMode}`,
			this.state.isEditing ? `${classBlock}--editing` : '',
			this.state.isExpanded ? `${classBlock}--expanded` : '',
			this.state.isDragging ? `${classBlock}--dragging` : '',
			this.state.isActivating ? `${classBlock}--touch-active` : '',
			(this.state.board  as IsEditing).isEditing ? `${classBlock}--board-editing` : '', // Добавляем модификатор для режима редактирования доски
			this.app.getTaskHighlight(this.props.task)? `${classBlock}--highlighted` : '', // Добавляем модификатор для подсветки
		].filter(Boolean).join(' ');

		// Определяем курсор
		const cursor = this.taskEditController.state.isEditing || (this.state.board as IsEditing).isEditing ? 'default' : 'grab';
		return (
			<>
				{this.state.isEditing && <Backdrop onClick={this.handleCloseEditor} className="task-card__backdrop"/>}
				<div
					ref={this.cardRef}
					className={`task-card ${rootModifiers}`}
					//onDoubleClick={this.handleDoubleClick}
					style={{cursor}}
					data-task-id={this.props.task.id}
				>
					{this.state.isEditing && this.renderEditor()}
					{this.renderCardContent()}
				</div>
			</>
		);
	}

	renderEditor() {
		const {task} = this.props;
		return (
			<InlineTaskEditor
				cardRef={this.cardRef}
				task={task}
				onClose={this.handleCloseEditor}
				onSave={this.handleSaveTask}
				onDelete={this.handleDeleteTask}
			/>
		);
	}

	renderCardContent() {
		return (
			<>
				{this.renderHeader()}
				{this.renderDescription()}
				{this.renderMetaSection()}
				{false&& this.state.viewMode === ViewModeEnum.List && this.renderDesktopFooter()}
			</>
		);
	}

	renderHeader() {
		return (
			<div className="task-card__header">
				<h4
					className={`task-card__title task-card__title--${this.state.viewMode}`}
					onClick={this.handleTitleClick}
					style={{cursor: !this.state.isEditing ? 'text' : 'default'}}
				>
					{this.props.task.title}
				</h4>
			</div>
		);
	}

	renderDescription() {
		return null;
		if (!this.props.task.description) return null;

		return (
			<div className="task-card__description">
				<button
					className="task-card__description-toggle"
					onClick={this.toggleDescription}
					type="button"
				>
					<span className="task-card__toggle-icon">
						{this.state.isExpanded ? '▲' : '▼'}
					</span>
					<span className="task-card__toggle-text">Описание</span>
				</button>

				{this.state.isExpanded && (
					<div className="task-card__description-content">
						{this.props.task.description}
					</div>
				)}
			</div>
		);
	}

	renderMetaSection() {
		const hasAttachments = this.props.task.attachments.length > 0;
		const hasComments = this.props.task.comments.length > 0;

		return (
			<div className="task-card__meta">
				<div className="task-card__meta-left">
					{hasAttachments || hasComments ? this.renderIcons() : null}
				</div>

				{this.state.viewMode === ViewModeEnum.List && this.renderMobileActions()}
			</div>
		);
	}

	renderIcons() {
		const hasAttachments = this.props.task.attachments.length > 0;
		const hasComments = this.props.task.comments.length > 0;

		return (
			<div className="task-card__icons">
				{hasAttachments && (
					<span className="task-card__icon" title="Вложения">{Icons.Attachment}{this.props.task.attachments.length}</span>
				)}
				{hasComments && (
					<span className="task-card__icon" title="Комментарии">{Icons.Comment}{this.props.task.comments.length}</span>
				)}
			</div>
		);
	}

	renderMobileActions() {
		return (
			<div className="task-card__actions">
				<button
					className="task-card__action-button"
					type="button"
					aria-label="Действия"
				>
					{Icons.Menu}
				</button>
			</div>
		);
	}

	renderDesktopFooter() {
		const syncStatus = this.getSyncStatus(this.props.task);
		const actions :CardActionSpec[] = [
			{id: 'attachment', icon: Icons.Attachment, title: 'Вложения', onClick: this.handleAttachmentClick},
			{id: 'edit', icon: Icons.EditDoc, title: 'Полное редактирование', onClick: this.handleFullEditClick},
			{id: 'comment', icon: Icons.Comment, title: 'Комментарии'},
			{id: 'user', icon: Icons.User, title: 'Участники',},
			{
				id: 'sync',
				title: syncStatus.text,
				icon: syncStatus.icon,
				className: `task-card__sync-icon ${syncStatus.className}`,
				ariaLabel: syncStatus.text
			}
		];

		return (
			<div className="task-card__footer">
				<div className="task-card__footer-content">
					{this.props.task.serverId && (
						<span className={`task-card__id task-card__id--${this.state.viewMode}`}>#{this.props.task.serverId}</span>
					)}
				</div>

				<div className="task-card__footer-actions">
					{actions.map(config => (
						<button
							key={config.id}
							className={`task-card__footer-icon ${config.className || ''}`}
							onClick={config.onClick}
							title={config.title}
							type="button"
							aria-label={config.ariaLabel || config.title}
						>
							{config.icon}
						</button>
					))}
				</div>
			</div>
		);
	}
}

export default TaskCard;